import React from 'react';
import { Alert } from 'reactstrap';

import * as JKYModel from '../custom-types';
import { codeProblem, signInWithCode } from '../utils/TwoStep';

import './SignupView.scss';

interface TwoStepCodeStepProps {
  mfaToken: string;
  onSignedIn: (user: JKYModel.User, token: string) => void;
  onCancel: () => void;
}

/**
 * The password (or Google) checked out and the account has two-step sign-in on: they type
 * the six digits their authenticator app shows, or one of their recovery codes.
 */
const TwoStepCodeStep = ({ mfaToken, onSignedIn, onCancel }: TwoStepCodeStepProps) => {
  const [code, setCode] = React.useState('');
  const [useRecovery, setUseRecovery] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const problem = codeProblem(code);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { user, token } = await signInWithCode(mfaToken, code);
      onSignedIn(user, token);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      // The password step is ten minutes old: back to it.
      if (err.code === 'MFA_EXPIRED') {
        setTimeout(onCancel, 2500);
      }
    }
  };

  return (
    <div className="public-page-formwrapper">
      <h1 className="public-page-formaccess">Enter Your Code</h1>
      <form onSubmit={handleSubmit} noValidate className="public-page-form">
        <p className="google-step-intro">
          {useRecovery
            ? 'Type one of the recovery codes you saved when you turned on two-step sign-in. Each one works once.'
            : 'Open your authenticator app and type the 6-digit code it shows for Jookable.'}
        </p>
        {error && <Alert color="danger">{error}</Alert>}
        {useRecovery ? (
          <input
            key="recovery"
            type="text"
            name="code"
            placeholder="xxxx-xxxx-xxxx"
            aria-label="Recovery code"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={20}
            autoFocus
            value={code}
            onChange={e => setCode(e.target.value)}
          />
        ) : (
          <input
            key="app"
            type="text"
            name="code"
            className="two-step-digits"
            placeholder="123456"
            aria-label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={7}
            autoFocus
            value={code}
            onChange={e => setCode(e.target.value.replace(/[^0-9 ]/g, ''))}
          />
        )}
        <button className="button" disabled={loading}>
          {loading ? 'Checking…' : 'Sign In'}
        </button>
      </form>
      <div className="signin-link-wrapper">
        <button
          type="button"
          className="signin-link google-step-cancel"
          disabled={loading}
          onClick={() => {
            setUseRecovery(!useRecovery);
            setCode('');
            setError('');
          }}
        >
          {useRecovery ? 'Use my authenticator app' : 'Lost your phone? Use a recovery code'}
        </button>
        <br />
        <br />
        <button type="button" className="signin-link google-step-cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default TwoStepCodeStep;
